// Small, dependency-free color math for deriving semantic tokens from a
// unit's 5-color palette and validating WCAG contrast — see
// derive-tokens.ts and docs/site-builder-overhaul.md's design-system pass.

export function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return [r, g, b];
}

function componentToHex(value: number): string {
  return Math.round(Math.min(255, Math.max(0, value)))
    .toString(16)
    .padStart(2, '0');
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

// WCAG relative luminance — https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((channel) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// WCAG contrast ratio — https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
// Always >= 1 (identical colors) and <= 21 (black on white).
export function contrastRatio(hexA: string, hexB: string): number {
  const lA = relativeLuminance(hexA);
  const lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB);
  const darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

// AA thresholds — 4.5:1 for normal text, 3:1 for large text (18pt+/14pt+
// bold) and UI components. https://www.w3.org/TR/WCAG21/#contrast-minimum
export function meetsWcagAA(ratio: number, isLargeOrUi = false): boolean {
  return ratio >= (isLargeOrUi ? 3 : 4.5);
}

// Picks whichever of two candidate text colors has the higher contrast
// against a given background — used to choose accessible text-on-accent
// without assuming the unit's accent color is light or dark.
export function pickAccessibleText(background: string, optionA: string, optionB: string): string {
  return contrastRatio(background, optionA) >= contrastRatio(background, optionB) ? optionA : optionB;
}

function hexToHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    default:
      h = (r - g) / d + 4;
  }
  return [h * 60, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  if (s === 0) {
    const gray = l * 255;
    return rgbToHex(gray, gray, gray);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hueToRgb = (t0: number) => {
    let t = t0;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const hNorm = h / 360;
  return rgbToHex(hueToRgb(hNorm + 1 / 3) * 255, hueToRgb(hNorm) * 255, hueToRgb(hNorm - 1 / 3) * 255);
}

// Shifts a hex color's HSL lightness by `delta` (roughly -1..1) — used for
// hover/active accent shades. Clamped to a sane visible range so a very
// light or very dark accent doesn't shift into pure white/black.
export function adjustLightness(hex: string, delta: number): string {
  const [h, s, l] = hexToHsl(hex);
  const next = Math.min(0.92, Math.max(0.08, l + delta));
  return hslToHex(h, s, next);
}

// Simple linear blend between two hex colors — used for muted/disabled
// tones (e.g. 50% between darkest and lightest).
export function mixHex(hexA: string, hexB: string, weightB: number): string {
  const [r1, g1, b1] = hexToRgb(hexA);
  const [r2, g2, b2] = hexToRgb(hexB);
  const w = Math.min(1, Math.max(0, weightB));
  return rgbToHex(r1 + (r2 - r1) * w, g1 + (g2 - g1) * w, b1 + (b2 - b1) * w);
}
